export const completionModes = {
  direct: "Direct",
  taskScan: "TaskScan",
};

export const stopResultValues = {
  correct: "correct",
  incident: "incident",
};

export const routeStopFinalizeStatuses = {
  finalized: "FINALIZADO",
  incident: "INCIDENCIA",
};

export function parseCompletionMode(value) {
  return value?.trim?.().toLowerCase() === "taskscan"
    ? completionModes.taskScan
    : completionModes.direct;
}

export function buildFinishChips({ routeStop, draft, date = new Date() }) {
  const stop = routeStop?.stop;
  const packageCount = stop?.totalTasks ?? draft?.tasks?.length ?? 0;
  const packageWeight = sumTaskWeights(draft?.tasks ?? []);

  return [
    formatDateChip(date),
    formatPackageCount(packageCount),
    formatPackageWeight(packageWeight),
  ].filter(Boolean);
}

export function buildRouteStopIncidentOptions(incidentEvents, routeStop) {
  const stop = routeStop?.stop;
  const scope = resolveRouteStopScope(stop?.type ?? "");
  const validEvents = incidentEvents.filter(
    (event) =>
      equalsText(event.type, "INCIDENCIA") &&
      equalsText(event.scope, scope) &&
      equalsText(event.entity, "PARADA") &&
      event.code?.trim() &&
      event.description?.trim(),
  );

  return buildIncidentOptionsForEvents(validEvents);
}

export function createFinalizePayload({
  completionMode,
  selectedStopResult,
  incident,
  observations,
  draft,
}) {
  const status =
    selectedStopResult === stopResultValues.incident
      ? routeStopFinalizeStatuses.incident
      : routeStopFinalizeStatuses.finalized;

  const isIncident = status === routeStopFinalizeStatuses.incident;

  return {
    delivery_info: {
      observations: normalizeText(observations),
      status,
      status_description: isIncident
        ? normalizeText(incident?.description)
        : "Finalizado",
      ...(isIncident ? { status_code: normalizeText(incident?.code) } : {}),
    },
    location: null,
    tasks:
      completionMode === completionModes.taskScan
        ? normalizeTaskDrafts(draft?.tasks ?? [])
        : [],
    images: [],
  };
}

export function canFinalize({
  isSubmitting,
  routeStop,
  completionMode,
  selectedStopResult,
  selectedIncident,
  draft,
}) {
  if (isSubmitting || !routeStop) {
    return false;
  }

  if (completionMode === completionModes.taskScan && !draft) {
    return false;
  }

  if (selectedStopResult === stopResultValues.incident) {
    return Boolean(
      selectedIncident?.description?.trim() && selectedIncident?.code?.trim(),
    );
  }

  return true;
}

export function finalizeHelperText({
  completionMode,
  selectedStopResult,
  selectedIncident,
  draft,
}) {
  if (completionMode === completionModes.taskScan && !draft) {
    return "No se encontro el borrador de paquetes.";
  }

  if (
    selectedStopResult === stopResultValues.incident &&
    !selectedIncident?.description?.trim()
  ) {
    return "Selecciona la incidencia para finalizar la parada.";
  }

  return "Listo para finalizar sin firma ni fotos.";
}

export function normalizeStoredRouteStopContext(context) {
  if (!context) {
    return null;
  }

  if (context.stop) {
    return context;
  }

  return {
    key: context.stopId || context.id,
    stop: context,
    displayAddress:
      context.addressNormalized?.displayAddress ?? context.displayAddress ?? "",
    scheduleLabel: "",
    contact: context.contact?.name ?? "",
  };
}

function normalizeTaskDrafts(tasks) {
  return tasks.map((task) => {
    const isIncident = task.status === "INCIDENCIA";

    return {
      _id: task.id,
      stop_id: task.stopId,
      shipment_external_id: task.shipmentExternalId,
      shipment_reference: task.shipmentReference,
      barcode: task.barcode,
      status: task.status,
      status_description: isIncident
        ? normalizeText(task.incidentDescription)
        : "Finalizado",
      ...(isIncident ? { status_code: normalizeText(task.incidentCode) } : {}),
    };
  });
}

function buildIncidentOptionsForEvents(events) {
  const duplicateDescriptions = events.reduce((lookup, event) => {
    const key = event.description.trim().toLowerCase();
    lookup.set(key, (lookup.get(key) ?? 0) + 1);
    return lookup;
  }, new Map());

  return events.map((event) => {
    const description = event.description.trim();
    const code = event.code.trim();
    const hasDuplicateDescription =
      (duplicateDescriptions.get(description.toLowerCase()) ?? 0) > 1;

    return {
      code,
      description,
      displayText: hasDuplicateDescription
        ? `${description} (${code})`
        : description,
    };
  });
}

function resolveRouteStopScope(routeStopType) {
  const normalizedType = routeStopType.trim().toLowerCase();
  return normalizedType === "recogida" || normalizedType === "recoleccion"
    ? "RECOGIDA"
    : "ENTREGA";
}

function formatDateChip(date) {
  const monthNames = [
    "ene",
    "feb",
    "mar",
    "abr",
    "may",
    "jun",
    "jul",
    "ago",
    "sep",
    "oct",
    "nov",
    "dic",
  ];
  const day = String(date.getDate()).padStart(2, "0");
  return `${day} ${monthNames[date.getMonth()]}`;
}

function formatPackageCount(packageCount) {
  const normalizedCount = Math.max(0, packageCount);
  const noun = normalizedCount === 1 ? "Paquete" : "Paquetes";
  return `${normalizedCount} ${noun}`;
}

function formatPackageWeight(weight) {
  const normalizedWeight = Number(weight);

  if (!Number.isFinite(normalizedWeight) || normalizedWeight <= 0) {
    return "";
  }

  return `${new Intl.NumberFormat("en-US", {
    maximumFractionDigits: 1,
    minimumFractionDigits: 0,
  }).format(normalizedWeight)} Kg`;
}

function sumTaskWeights(tasks) {
  return tasks.reduce((total, task) => {
    const weight = Number(task?.weight);

    if (!Number.isFinite(weight) || weight <= 0) {
      return total;
    }

    return total + weight;
  }, 0);
}

function equalsText(left, right) {
  return left?.trim?.().toLowerCase() === right?.trim?.().toLowerCase();
}

function normalizeText(value) {
  return value?.trim?.() ?? "";
}
